/** `W-40c`·`W-40d` 판정의 웹 쪽 자리. **판정과 문구의 정본은 `@makit/shared` 의
 * `export-decline.ts` 다**(PRD `7.7.5a` · `7.6.14b` · `13.1` D-93).
 *
 * 앱 `apps/mobile/src/lib/export-decline.ts` 와 같은 모양이다. 이 자리가 더하는 것은 둘이다.
 *
 * 1. **파일 수를 셀 판매처로 화면이 서 있는 판매처를 건다.** 웹의 그 값은 `lib/channel-spec`
 *    의 `CHANNEL` 이고 앱의 `CURRENT_CHANNEL` 과 출처가 같다(`DEFAULT_SALES_CHANNEL`).
 * 2. **`만들지 않기` 를 서버에 적는 호출.** `lib/api.ts` 에 아직 없어 여기 둔다.
 *
 * ⛔ 판정이나 셀러가 읽는 문장을 이 파일에 다시 적지 않는다. 적으면 앱과 웹이 같은 갈래를
 * 두 벌로 갖고, shared 시험은 그중 하나만 본다.
 */

import {
  exportDeclineView as sharedExportDeclineView,
  jobEndpoints,
  type ApiErrorBody,
  type DecideImageRecallRequest,
  type DetailJobView,
  type ExportDeclinePage,
  type ExportDeclineView,
} from "@makit/shared";

import { ApiError, getBaseUrl } from "./api";
import { CHANNEL } from "./channel-spec";

/** 작업 하나에서 `W-40c` 가 무엇을 말할지. `null` 이면 평상 `W-40` 이다. 판정은 shared 가
 *  하고, 이 자리는 화면이 서 있는 판매처로 파일 수를 세게 한다. */
export function exportDeclineView(job: DetailJobView): ExportDeclineView | null {
  return sharedExportDeclineView(job, CHANNEL);
}

/** `W-40d` 의 `만들지 않기`. **앱 `A-52d` 와 같은 주소에 같은 본문을 보낸다** —
 *  `POST /api/jobs/:jobId/images/:imageId/decision` · `{ decision: "DECLINE" }`.
 *
 * ⭐ **그 주소는 공급자를 부르지 않는다**(`apps/api/src/routes/recall-decision.ts` 머리말).
 * 그래서 장마다 한 번씩 불러도 원가가 0원이다. 호출 수의 상한은 **그 화면에 선 행 수**이고
 * 그것은 올린 장수를 넘지 못한다(`7.6.14b` 는 접지 않고 전부 행으로 세운다).
 *
 * ⛔ **묶어 보내는 주소를 새로 만들지 않는다.** 같은 개념의 입구가 둘이 되면 둘이 갈리는 날
 * 한쪽만 이력을 남긴다. 앱도 장마다 한 번씩 부른다.
 *
 * ⚠️ **하나라도 실패하면 거기서 멈추고 던진다.** 이미 적힌 장은 서버에 남고, 화면이 작업을
 * 다시 읽으면 그 장은 묻는 목록에서 빠진다(`imagesAwaitingRecallDecision`). 남은 장만 다시
 * 묻게 되므로 판이 반쯤 나가는 일은 없다 — 내보내기는 이 호출이 전부 끝난 뒤에만 부른다.
 *
 * ⚠️ `lib/api.ts` 의 `request` 는 공개돼 있지 않아 같은 규약(쿠키 포함 · `ApiError`)으로
 * 따로 부른다. 그래서 **401 이 로그인 화면으로 보내는 처리(`notifyUnauthorized`)를 지나지
 * 않는다.** 이 호출이 401 을 받으면 화면은 내보내기 실패 알림을 띄우고, 다음 요청이 그
 * 처리를 탄다. 이 호출이 `api.ts` 로 옮겨 가면 이 문단이 함께 빠진다. */
export async function declineExportPages(
  jobId: string,
  pages: readonly Pick<ExportDeclinePage, "imageId">[],
): Promise<void> {
  for (const page of pages) {
    const payload: DecideImageRecallRequest = { decision: "DECLINE" };
    let res: Response;
    try {
      res = await fetch(`${getBaseUrl()}${jobEndpoints.decideImageRecall(jobId, page.imageId)}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    } catch {
      // 네트워크가 끊긴 경우다. `api.ts` 와 같이 상태 코드 0 으로 둔다.
      throw new ApiError(0);
    }
    if (!res.ok) throw new ApiError(res.status, await readErrorCode(res));
  }
}

async function readErrorCode(res: Response): Promise<string | undefined> {
  try {
    return ((await res.json()) as ApiErrorBody).code;
  } catch {
    return undefined;
  }
}
